import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import { techDocsReportIssueAddonModule } from '@backstage/plugin-techdocs-module-addons-contrib/alpha';

import { createApp } from '@backstage/frontend-defaults';

import { apisModule } from './modules/apisModule';
import { iconsModule } from './modules/iconsModule';
import { themesModule } from './modules/themesModule';
import { signInModule } from './modules/signInModule';
import { navModule } from './modules/nav';
import { customPagesModule } from './modules/customPagesModule';
import { searchModule } from './modules/searchModule';

/**
 * All routes are now provided natively by auto-discovered alpha plugins
 * via `app.packages: all` in app-config.yaml.  Because all plugin packages
 * listed in packages/app/package.json carry a `backstage.role: "frontend-plugin"`
 * field and their `./alpha` export has the `@backstage/FrontendPlugin` marker,
 * the Backstage CLI webpack auto-discovery loads the alpha entrypoint for every
 * dependency.  This means the following routes are registered natively without
 * any explicit feature entry in this file:
 *
 *  /catalog                  → catalogPlugin (alpha)
 *  /catalog/:namespace/:kind/:name  → catalogPlugin entity page (alpha)
 *  /catalog-graph            → catalogGraphPlugin (alpha)
 *  /api-docs                 → apiDocsPlugin (alpha)
 *  /docs, /docs/:...         → techdocsPlugin (alpha)
 *  /search                   → searchPlugin (alpha)
 *  /settings                 → userSettingsPlugin (alpha)
 *  /notifications            → notificationsPlugin (alpha)
 *  /catalog-import           → catalogImportPlugin (alpha)
 *
 * All entity-content / entity-card extensions are provided natively by:
 *  entity-content:catalog/overview          – catalog alpha
 *  entity-content:techdocs                  – techdocs alpha
 *  entity-content:api-docs/apis             – api-docs alpha
 *  entity-content:api-docs/definition       – api-docs alpha
 *  entity-content:github-actions            – @backstage-community/plugin-github-actions alpha
 *  entity-content:kubernetes/kubernetes     – kubernetes alpha
 *  entity-card:catalog-graph/relations      – catalog-graph alpha
 *  entity-card:org/group-profile, ...       – org alpha
 *  entity-card:api-docs/*, entity-card:catalog/*, etc.
 *
 * The legacy EntityPage.tsx has been deleted — all entity UI is provided
 * natively by the auto-discovered alpha plugins listed above.
 */

const app = createApp({
    features: [
        apisModule,
        iconsModule,
        themesModule,
        signInModule,
        navModule,
        customPagesModule,
        searchModule,
        techDocsReportIssueAddonModule,
    ],
    bindRoutes({ bind }) {
        bind(catalogPlugin.externalRoutes, {
            createComponent: scaffolderPlugin.routes.root,
            viewTechDoc: techdocsPlugin.routes.docRoot,
            createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
        });
        bind(apiDocsPlugin.externalRoutes, {
            registerApi: catalogImportPlugin.routes.importPage,
        });
        bind(scaffolderPlugin.externalRoutes, {
            registerComponent: catalogImportPlugin.routes.importPage,
            viewTechDoc: techdocsPlugin.routes.docRoot,
        });
        bind(orgPlugin.externalRoutes, {
            catalogIndex: catalogPlugin.routes.catalogIndex,
        });
    },
});

export default app.createRoot();
